/**
 * LayoutRenderers — container, card, form-container, flex-container, modal, list.
 *
 * All container types share the child-resolution pattern and ContainerModalWrapper
 * integration. This file fixes all 11 Group-A `as any` casts for _childComponents.
 */
import React from "react";
import { Component, ComponentType, Viewport } from "@/types";
import type { ColorScheme } from "@/types/color-scheme";
import { cn } from "@/lib/utils";
import { ComponentWrapper } from "../ComponentWrapper";
import { SharedModalComponent } from "../SharedModalComponent";
import {
  ContainerModalWrapper,
  getModalConfig,
} from "../ContainerModalWrapper";
import { FlexContainer } from "@/components/editor/FlexContainer";
import { Card } from "@/components/editor/Card";
import { FormContainer } from "@/components/editor/FormContainer";
import {
  resolveChildComponents,
  resolveViewerMode,
  toCssProperty,
  JUSTIFY_CONTENT_VALUES,
  ALIGN_ITEMS_VALUES,
} from "./utils";

// LayoutRenderers and SharedComponentRenderer are circularly dependent:
// SharedComponentRenderer imports LayoutRenderers, and LayoutRenderers
// needs SharedComponentRenderer to render modal children recursively.
// This is safe in ESM (live bindings) because SharedComponentRenderer is
// accessed only inside React render functions — never at module init time.
// See roadmap Section 9 Risk 3 for the full analysis.
import { SharedComponentRenderer } from "../SharedComponentRenderer";

export interface LayoutRendererProps {
  component: Component;
  viewport?: Viewport;
  allComponents?: Component[];
  activeColorScheme?: ColorScheme;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal shared modal children renderer
// ─────────────────────────────────────────────────────────────────────────────

function ModalChildrenWrapper({
  children,
  allComponents,
  viewport,
  activeColorScheme,
}: {
  children: Component[];
  allComponents: Component[] | undefined;
  viewport: Viewport | undefined;
  activeColorScheme?: ColorScheme;
}): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {children.map((child) => (
        <SharedComponentRenderer
          key={child.id}
          component={child}
          allComponents={allComponents}
          viewport={viewport}
          activeColorScheme={activeColorScheme}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SharedContainerComponent (fixes Group-A _childComponents + Group-C CSS casts)
// ─────────────────────────────────────────────────────────────────────────────

const ContainerRenderer: React.FC<{
  component: Component;
  allComponents?: Component[];
  activeColorScheme?: ColorScheme;
}> = React.memo(({ component, allComponents, activeColorScheme }) => {
  const { properties = {}, style } = component;
  // Replaces all (component as any)._childComponents casts (Group A)
  const childComponents = resolveChildComponents(component, allComponents);

  return (
    <div
      className={cn(
        "w-full h-full flex",
        properties.direction === "row" ? "flex-row" : "flex-col",
      )}
      style={{
        ...style,
        padding:
          typeof properties.padding === "number" ||
          typeof properties.padding === "string"
            ? properties.padding
            : undefined,
        backgroundColor:
          (typeof properties.backgroundColor === "string"
            ? properties.backgroundColor
            : undefined) ||
          (typeof style?.backgroundColor === "string"
            ? style.backgroundColor
            : undefined),
        borderRadius:
          (typeof properties.borderRadius === "string" ||
          typeof properties.borderRadius === "number"
            ? properties.borderRadius
            : undefined) ||
          (typeof style?.borderRadius === "string" ||
          typeof style?.borderRadius === "number"
            ? style.borderRadius
            : undefined),
        gap:
          typeof properties.gap === "string" ||
          typeof properties.gap === "number"
            ? properties.gap
            : undefined,
        // Replaces (properties.justifyContent as any) — Group C, line 4653
        justifyContent: toCssProperty<"justifyContent">(
          properties.justifyContent,
          JUSTIFY_CONTENT_VALUES,
          "flex-start",
        ),
        // Replaces (properties.alignItems as any) — Group C, line 4654
        alignItems: toCssProperty<"alignItems">(
          properties.alignItems,
          ALIGN_ITEMS_VALUES,
          "stretch",
        ),
      }}
    >
      {childComponents.map((child) => (
        <ComponentWrapper
          key={child.id}
          component={child}
          isFlexChild={true}
          flexDirection={
            properties.direction === "row" ? "row" : "column"
          }
          activeColorScheme={activeColorScheme}
        />
      ))}
    </div>
  );
});

ContainerRenderer.displayName = "ContainerRenderer";

// ─────────────────────────────────────────────────────────────────────────────
// Public dispatcher
// ─────────────────────────────────────────────────────────────────────────────

export function LayoutRenderers({
  component,
  allComponents,
  viewport,
  activeColorScheme,
}: LayoutRendererProps): JSX.Element {
  const isViewerMode = resolveViewerMode(viewport);

  switch (component.type) {
    case ComponentType.CONTAINER: {
      const modalConfig = getModalConfig(component);
      if (modalConfig) {
        let children = resolveChildComponents(component, allComponents);
        if (children.length === 0 && allComponents) {
          children = allComponents.filter((c) => c.parentId === component.id);
        }
        return (
          <ContainerModalWrapper component={component} modalConfig={modalConfig}>
            <ModalChildrenWrapper
              children={children}
              allComponents={allComponents}
              viewport={viewport}
              activeColorScheme={activeColorScheme}
            />
          </ContainerModalWrapper>
        );
      }
      return (
        <ContainerRenderer
          component={component}
          allComponents={allComponents}
          activeColorScheme={activeColorScheme}
        />
      );
    }

    case ComponentType.FLEX_CONTAINER: {
      const childComponents = resolveChildComponents(component, allComponents);
      const modalConfig = getModalConfig(component);
      if (modalConfig) {
        let modalChildren = childComponents;
        if (modalChildren.length === 0 && allComponents) {
          modalChildren = allComponents.filter(
            (c) => c.parentId === component.id,
          );
        }
        return (
          <ContainerModalWrapper component={component} modalConfig={modalConfig}>
            <ModalChildrenWrapper
              children={modalChildren}
              allComponents={allComponents}
              viewport={viewport}
              activeColorScheme={activeColorScheme}
            />
          </ContainerModalWrapper>
        );
      }
      return (
        <FlexContainer
          component={component}
          children={childComponents}
          isPreview={isViewerMode}
          viewport={viewport}
          activeColorScheme={activeColorScheme}
        />
      );
    }

    case ComponentType.CARD: {
      const childComponents = resolveChildComponents(component, allComponents);
      const modalConfig = getModalConfig(component);
      if (modalConfig) {
        let modalChildren = childComponents;
        if (modalChildren.length === 0 && allComponents) {
          modalChildren = allComponents.filter(
            (c) => c.parentId === component.id,
          );
        }
        return (
          <ContainerModalWrapper component={component} modalConfig={modalConfig}>
            <ModalChildrenWrapper
              children={modalChildren}
              allComponents={allComponents}
              viewport={viewport}
              activeColorScheme={activeColorScheme}
            />
          </ContainerModalWrapper>
        );
      }
      return (
        <Card
          component={component}
          children={childComponents}
          isPreview={isViewerMode}
          viewport={viewport}
          activeColorScheme={activeColorScheme}
        />
      );
    }

    case ComponentType.FORM_CONTAINER: {
      let childComponents = resolveChildComponents(component, allComponents);
      if (childComponents.length === 0 && allComponents) {
        childComponents = allComponents.filter(
          (c) => c.parentId === component.id,
        );
      }
      const modalConfig = getModalConfig(component);
      if (modalConfig) {
        return (
          <ContainerModalWrapper component={component} modalConfig={modalConfig}>
            <ModalChildrenWrapper
              children={childComponents}
              allComponents={allComponents}
              viewport={viewport}
              activeColorScheme={activeColorScheme}
            />
          </ContainerModalWrapper>
        );
      }
      return (
        <FormContainer
          component={component}
          children={childComponents}
          isPreview={isViewerMode}
          viewport={viewport}
          activeColorScheme={activeColorScheme}
        />
      );
    }

    case ComponentType.MODAL: {
      const childComponents = resolveChildComponents(component, allComponents);
      return (
        <SharedModalComponent component={component} isViewerMode={isViewerMode}>
          {childComponents.map((child) => (
            <SharedComponentRenderer
              key={child.id}
              component={child}
              allComponents={allComponents}
              viewport={viewport}
              activeColorScheme={activeColorScheme}
            />
          ))}
        </SharedModalComponent>
      );
    }

    case ComponentType.LIST:
    default:
      // LIST falls back to the container renderer (same as original)
      console.log(
        "📜 [LayoutRenderers] Rendering LIST as container:",
        component.id,
      );
      return (
        <ContainerRenderer
          component={component}
          allComponents={allComponents}
          activeColorScheme={activeColorScheme}
        />
      );
  }
}
